import React, { useEffect, useState } from "react";
import StatTile, { formatCompact, formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import FunnelChart from "../charts/FunnelChart";
import TrendLineChart from "../charts/TrendLineChart";
import { pick } from "../metricsAdapters";
import { getInvalidCreditNoteOverview } from "../../../services/metricsApi";
import "../Analytics.css";

// Field names for status_funnel / trend_analysis are best-effort inferred
// (Analytics Phase 2 plan) — pick() hedges each one.
const FUNNEL_STAGE_CANDIDATES = [
  { key: "total_invalid", label: "Total Invalid", paths: ["total_invalid", "raised", "total_raised"] },
  { key: "total_credit_note_applicable", label: "Credit Note Applicable", paths: ["total_credit_note_applicable", "under_review", "in_review"] },
  { key: "credit_note_available", label: "Credit Note Available", paths: ["credit_note_available", "credit_note_issued", "issued"] },
  { key: "credit_note_pending", label: "Credit Note Pending", paths: ["credit_note_pending", "closed", "resolved"] },
];

const InvalidCreditNoteSection = ({ id, filters, refreshKey }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    getInvalidCreditNoteOverview(filters)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filters, refreshKey]);

  const { loading, data, error } = state;
  const funnelSource = pick(data, ["status_funnel"], {}) || {};
  const stages = FUNNEL_STAGE_CANDIDATES.map((s) => ({
    key: s.key,
    label: s.label,
    count: Number(pick(funnelSource, s.paths, 0)) || 0,
  }));
  const hasFunnelData = Object.keys(funnelSource).length > 0;

  const trendSource = pick(data, ["trend_analysis"], []) || [];
  const xLabels = trendSource.map((row) => pick(row, ["month", "period", "label"], ""));
  const trendSeries = [
    {
      key: "invalid",
      label: "Invalid Requests",
      values: trendSource.map((row) => Number(pick(row, ["total_invalid", "invalid_count", "invalid", "count"], 0)) || 0),
    },
  ];

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">Invalid &amp; Credit Note Overview</h2>
      </div>

      <WarningBanner warning={data?.warning} />
      {error && <div className="overview-card-error">Failed to load Invalid & Credit Note Overview: {error}</div>}
      {loading && !data && <div className="overview-card-loading">Loading…</div>}

      {data && (
        <>
          <div className="stat-tile-row">
            <StatTile
              label="Invalid Requests"
              value={formatCount(pick(data, ["kpi_cards.total_invalid_requests.count", "kpi_cards.total_invalid_requests"], 0))}
            />
            <StatTile
              label="Credit Note Applicable"
              value={formatCount(pick(data, ["kpi_cards.total_credit_note_applicable.count", "kpi_cards.total_credit_note_applicable"], 0))}
            />
            <StatTile
              label="Credit Note Available"
              value={formatCount(pick(data, ["kpi_cards.credit_note_available.count", "kpi_cards.credit_note_available"], 0))}
              status="good"
            />
            <StatTile
              label="Credit Note Pending"
              value={formatCount(pick(data, ["kpi_cards.credit_note_pending.count", "kpi_cards.credit_note_pending"], 0))}
              status="warning"
            />
            <StatTile label="Total Amount" value={formatCompact(pick(data, ["total_amount"], 0), { currency: true })} />
          </div>

          <div className="analytics-charts-row">
            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Status Funnel</h4>
              {hasFunnelData ? <FunnelChart stages={stages} /> : <div className="funnel-empty">No status funnel data for the current filters.</div>}
            </div>

            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Monthly Trend</h4>
              <TrendLineChart series={trendSeries} xLabels={xLabels} />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default InvalidCreditNoteSection;
